/**
 * What has happened to a database, whoever or whatever did it.
 *
 * The panel beside a row answers "what happened to *this* row". This answers the
 * question that panel cannot: what did that agent just do -- across forty rows
 * and three tables, none of which anybody knows to go and look at. It is the
 * reason the endpoints behind it record where a write came in through, and the
 * only place in the application that offers to put a recorded change back.
 */
import { useState } from 'react'
import { History } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DatabaseApplication } from '@/api/types'
import { mergedPagesBy } from '@/shared/lib/paged'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Skeleton } from '@/shared/ui/skeleton'
import { daySections, type ActivityFilters } from '../activity'
import { useDatabaseActivity, type RevertTarget } from '../queries'
import { ActivityFilterBar } from './activity-filters'
import { OperationRow } from './operation-row'
import { RevertDialog } from './revert-dialog'

export function ActivityDialog({
  database,
  workspaceId,
  open,
  onOpenChange,
}: {
  database: DatabaseApplication
  workspaceId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<ActivityFilters>({})
  const [reverting, setReverting] = useState<RevertTarget | null>(null)
  const activity = useDatabaseActivity(database.id, filters)

  // Keyed by the operation *and* the command: an operation that was undone
  // appears twice, once as it was done and once as the undo, and they are two
  // things that happened rather than one row listed twice.
  const operations = mergedPagesBy(
    activity.data,
    (operation) => `${operation.action_uuid}:${operation.action_command_type}`,
  )
  // Read once per render rather than per section: two sections built either
  // side of midnight would disagree about which day is "today".
  const sections = daySections(operations, new Date())

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('activity.title')}</DialogTitle>
            <DialogDescription>{t('activity.description')}</DialogDescription>
          </DialogHeader>

          <ActivityFilterBar
            tables={database.tables}
            filters={filters}
            onChange={setFilters}
          />

          <div className="min-h-0 flex-1 overflow-y-auto">
            {activity.isPending ? (
              <div className="space-y-3 py-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-2/3" />
              </div>
            ) : sections.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <History className="size-8 text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">{t('activity.empty')}</p>
              </div>
            ) : (
              sections.map((section) => (
                <section key={section.heading.key} className="mb-4">
                  <h3 className="sticky top-0 bg-background py-1 text-xs font-medium text-muted-foreground">
                    {section.heading.kind === 'on'
                      ? section.heading.text
                      : t(`activity.${section.heading.kind}`)}
                  </h3>
                  <ul className="space-y-2">
                    {section.operations.map((operation) => (
                      <OperationRow
                        key={operation.action_uuid + operation.action_command_type}
                        databaseId={database.id}
                        workspaceId={workspaceId}
                        operation={operation}
                        tables={database.tables}
                        onRevert={setReverting}
                      />
                    ))}
                  </ul>
                </section>
              ))
            )}

            {activity.hasNextPage && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={activity.isFetchingNextPage}
                onClick={() => {
                  void activity.fetchNextPage()
                }}
              >
                {t('activity.more')}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {reverting !== null && (
        <RevertDialog
          databaseId={database.id}
          target={reverting}
          onOpenChange={() => setReverting(null)}
        />
      )}
    </>
  )
}
